import { Building } from 'lucide-react'

import { Banner } from '@/components/ui/banner'
import { DrawerWithCloseButton } from '@/components/ui/drawer'
import { Separator } from '@/components/ui/separator'
import { formatAddress } from '../../../../models/address'
import { type COICertificateHolder, useCOISentLogs } from '../../../../models/coi'
import { CertificateHolderRowActions } from './CertificateHolderRowActions'
import { COISentLogTable } from './COISentLogTable'

interface CertificateHolderDrawerProps {
  holder: COICertificateHolder | null
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId?: string
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-base">{label}</p>
      <p className="text-base">{value || '-'}</p>
    </div>
  )
}

/** What this holder's COI schedules, in the same terms the edit dialog offers. */
function includedVehiclesLabel(holder: COICertificateHolder): string {
  // A hand-issued COI is never re-rendered, so its switches say nothing about the document on file.
  if (!holder.isAutomatic) {
    return holder.hasDocument ? 'Set on the uploaded document' : 'Set on the document Cai uploads'
  }
  if (holder.includeTrucks && holder.includeTrailers) return 'Trucks and trailers'
  if (holder.includeTrucks) return 'Trucks'
  if (holder.includeTrailers) return 'Trailers'
  return 'No vehicle schedule'
}

/**
 * A holder's details and the history of COIs sent to them.
 *
 * Actions are the same `CertificateHolderRowActions` the table row shows, rather
 * than a second copy of send/edit/upload/delete that could drift from it.
 */
export function CertificateHolderDrawer({ holder, open, onOpenChange, orgId }: CertificateHolderDrawerProps) {
  // The drawer is mounted with no holder until a row is clicked. Without
  // `enabled` this would fetch the org's whole sent log on every page load,
  // since an absent holder id means "all holders" to the endpoint.
  const { logs, isLoading: logsLoading } = useCOISentLogs(holder?.id, orgId, {
    enabled: !!holder,
  })

  if (!holder) return null

  return (
    <DrawerWithCloseButton
      open={open}
      onOpenChange={onOpenChange}
      handleClose={() => onOpenChange(false)}
      title={holder.name}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <Building className="text-muted-foreground size-5 shrink-0" />
            <h2 className="text-lg font-semibold">{holder.name}</h2>
          </div>
          <CertificateHolderRowActions holder={holder} orgId={orgId} />
        </div>

        {!holder.isAutomatic && !holder.hasDocument && (
          <Banner variant="warning">
            The COI document for this certificate holder has been requested. You&apos;ll be able to send it
            once it&apos;s ready.
          </Banner>
        )}

        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <Field label="Email" value={holder.email} />
          <Field label="Address" value={formatAddress(holder.address)} />
        </div>

        <Separator />

        <Field label="Included vehicles in COI" value={includedVehiclesLabel(holder)} />
        <Field label="Description of operations" value={holder.descriptionOfOperations} />

        <Separator />

        <div>
          <h3 className="mb-3 text-xl font-semibold">COI history</h3>
          <COISentLogTable
            logs={logs}
            isLoading={logsLoading}
            showHolder={false}
            emptyMessage="No COI has been sent to this certificate holder yet."
            orgId={orgId}
          />
        </div>
      </div>
    </DrawerWithCloseButton>
  )
}
